import { useCallback, useEffect, useState } from "react";
import { useLang } from "./LangContext";
import { STR } from "../lib/i18n";

const CONSENT_KEY = "sfv-consent"; // "granted" | "denied" | null
const GA_ID = "G-KWSR4R3FJK";

// Injicerar GA4 först EFTER samtycke – ingen data (inte ens IP) når Google innan dess.
function injectGA() {
  if (typeof window.gtag === "function") return;
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(s);
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    window.dataLayer.push(arguments);
  };
  window.gtag("js", new Date());
  window.gtag("config", GA_ID, { send_page_view: false });
  window.dispatchEvent(new Event("sfv-analytics-ready"));
}

export default function CookieConsent() {
  const { lang } = useLang();
  const t = STR[lang];
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const choice = localStorage.getItem(CONSENT_KEY);
    if (choice === "granted") {
      injectGA();
    } else if (!choice) {
      const timer = setTimeout(() => setVisible(true), 900);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, []);

  // Sidfotens "Cookieinställningar" öppnar bannern igen
  useEffect(() => {
    const reopen = () => setVisible(true);
    window.addEventListener("sfv-open-consent", reopen);
    return () => window.removeEventListener("sfv-open-consent", reopen);
  }, []);

  const choose = useCallback((granted) => {
    localStorage.setItem(CONSENT_KEY, granted ? "granted" : "denied");
    if (granted) injectGA();
    setVisible(false);
  }, []);

  return (
    <div
      data-testid="consent-banner"
      role="dialog"
      aria-label={t.consent.title}
      className={`fixed inset-x-0 bottom-0 z-[60] transition-transform duration-500 ease-out ${
        visible ? "translate-y-0" : "pointer-events-none translate-y-full"
      }`}
    >
      <div className="mx-auto max-w-3xl px-4 pb-4">
        <div className="border border-[#28332E] bg-[#161D1A] px-5 py-4 shadow-2xl shadow-black/50">
          <p className="overline-label text-[#C98755]">{t.consent.title}</p>
          <p className="mt-2 text-sm leading-relaxed text-[#EBE9E0]/85">{t.consent.body}</p>
          <div className="mt-4 flex flex-wrap gap-3">
            <button
              onClick={() => choose(true)}
              data-testid="consent-accept-button"
              className="bg-[#C98755] px-5 py-2 text-sm font-semibold text-[#0F1412] transition-colors duration-300 hover:bg-[#d99a68]"
            >
              {t.consent.accept}
            </button>
            <button
              onClick={() => choose(false)}
              data-testid="consent-decline-button"
              className="border border-[#28332E] px-5 py-2 text-sm font-semibold text-[#EBE9E0] transition-colors duration-300 hover:border-[#C98755] hover:text-[#C98755]"
            >
              {t.consent.decline}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
